import React, { useEffect, useMemo, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete, {
  AutocompleteInputChangeReason,
  AutocompleteProps,
  AutocompleteRenderOptionState,
} from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import { VariableSizeList } from "react-window";

import CheckboxV2 from "../Checkbox/CheckboxV2";
import TextInput from "../TextInput/TextInput";
import Text from "../Typography/Text";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import { pluralize } from "@src/utils/pluralize";

import styles from "./Combobox.module.css";

export type ComboboxOption<T extends string> = {
  label: string;
  value: T | undefined;
  meta?: {
    type?: string;
    isFixedOption?: boolean;
    group?: string;
  };
};

type ComboboxOptionTuple = [
  React.HTMLAttributes<HTMLLIElement>,
  ComboboxOption<string>,
  AutocompleteRenderOptionState
];

const optionsToShow = 10;
const optionHeight = 40;
const listboxHeight = optionHeight * optionsToShow;

/* eslint-disable react/prop-types */
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent({ children, ...props }, ref) {
  const itemData = children as ComboboxOptionTuple[];
  const itemCount = itemData.length;

  const height = useMemo(() => {
    if (itemCount < optionsToShow) {
      return itemCount * optionHeight;
    }

    return listboxHeight;
  }, [itemCount]);

  return (
    <div ref={ref}>
      <VariableSizeList
        className={toClassName(props.className, styles.listbox)}
        itemData={itemData}
        itemCount={itemCount}
        itemSize={() => optionHeight}
        height={height}
        width="100%"
        innerElementType={(innerElementProps) => {
          return <div {...innerElementProps} onMouseDown={props.onMouseDown} />;
        }}
        overscanCount={20}
      >
        {({ style, index, data }) => {
          const [props, option, state] = data[index];
          const { selected } = state;

          return (
            <li
              {...props}
              key={option.value}
              style={style}
              className={toClassName(props.className, styles.option, {
                [styles.selectedOption]: selected,
                [styles.showDivider]: option.meta?.isFixedOption,
              })}
            >
              <CheckboxV2 className={styles.checkbox} checked={selected} />
              <Text variant="body" color="text">
                {option.label}
              </Text>
            </li>
          );
        }}
      </VariableSizeList>
    </div>
  );
});

type MuiAutocompleteProps = AutocompleteProps<
  ComboboxOption<string>,
  true,
  false,
  false
>;

type MuiAutocompleteValue = MuiAutocompleteProps["value"];

export type ComboboxProps = {
  label?: string;
  resourceName?: string;
  options: ComboboxOption<string>[];
  value: MuiAutocompleteValue;
  onChange: (value: MuiAutocompleteValue) => void;
  preserveSearchOnSelect?: boolean;
  clearOthersOnUndefined?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
};

const Combobox: React.FC<React.PropsWithChildren<ComboboxProps>> = ({
  value,
  onChange,
  label,
  resourceName,
  options,
  preserveSearchOnSelect = false,
  clearOthersOnUndefined = false,
  error,
  helperText,
}) => {
  const [selectedCount, setSelectedCount] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Synchronize selectedCount with value prop
  useEffect(() => {
    setSelectedCount(value?.length || 0);
  }, [value]);

  const inputControlProps = preserveSearchOnSelect
    ? {
        inputValue,
        onInputChange: (
          _: React.SyntheticEvent,
          newInputValue: string,
          reason: AutocompleteInputChangeReason
        ) => {
          if (reason === "reset" && !isPopupOpen) {
            setInputValue("");
          } else if (reason !== "reset") {
            setInputValue(newInputValue);
          }
        },
      }
    : {};

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      blurOnSelect={false}
      classes={{ noOptions: styles.noOptions }}
      value={value}
      options={options}
      open={isPopupOpen}
      onOpen={() => setIsPopupOpen(true)}
      onClose={() => setIsPopupOpen(false)}
      onChange={(_, nextValue) => {
        if (clearOthersOnUndefined) {
          const hasNewUndefined =
            nextValue.some((option) => option.value === undefined) &&
            !value?.some((option) => option.value === undefined);

          const finalValue = hasNewUndefined
            ? nextValue.filter((option) => option.value === undefined)
            : nextValue.filter((option) => option.value !== undefined);

          setSelectedCount(finalValue.length);
          onChange?.(finalValue);
        } else {
          setSelectedCount(nextValue.length);
          onChange?.(nextValue);
        }
      }}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderInput={(params) => (
        <TextInput
          className={styles.input}
          label={label}
          error={error}
          helperText={helperText}
          {...params}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <SearchIcon className={styles.searchIcon} />
                </InputAdornment>

                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option, state) => [props, option, state]}
      renderTags={(value, _, ownerState) =>
        ownerState.popupOpen
          ? null
          : resourceName
          ? pluralize(value, resourceName, true)
          : `${value.length} selected`
      }
      clearIcon={
        <>
          {isPopupOpen && (
            <Text
              variant="body"
              color="textSubtle"
              className={styles.selectedText}
            >
              {selectedCount} selected
            </Text>
          )}
        </>
      }
      popupIcon={<KeyboardArrowDownIcon className={styles.arrowIcon} />}
      componentsProps={{
        clearIndicator: { disabled: true },
        paper: { className: styles.menuPaper, elevation: 0 },
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ],
        },
      }}
      ListboxComponent={ListboxComponent}
      {...inputControlProps}
    />
  );
};

export default Combobox;
